import { useNavigate, useParams } from "react-router-dom"
import { useCallback, useContext, useEffect, useState } from "react"
import { Button, Col, DatePicker, Divider, Flex, Input, InputNumber, List, Modal, Row, Spin } from "antd"
import { PlusCircleOutlined, ProfileOutlined } from "@ant-design/icons"
import { CompanyContext, CompanyRequestType, CompanyResponseType, UpdateCompanyContext } from "../../../context/companyContext"
import dayjs from 'dayjs'
import { DepartmentRequestType } from "../../../context/departmentContext"
import TextArea from "antd/es/input/TextArea"
import { PATH_INFO } from "../../util/helper"

const DEFAULT_DEPARTMENT: DepartmentRequestType = { id: null, name: '', brand_name: '', memo: '' }

const normalizeDepartments = (departments: unknown): DepartmentRequestType[] => {
  const rawList = (() => {
    if (Array.isArray(departments)) {
      return departments
    }
    if (departments && typeof departments === 'object' && 'data' in departments) {
      const data = (departments as { data?: unknown }).data
      if (Array.isArray(data)) {
        return data
      }
    }
    return []
  })()

  if (rawList.length === 0) {
    return [{ ...DEFAULT_DEPARTMENT }]
  }

  return rawList.map((dept: any) => ({
    id: dept?.id ?? null,
    name: dept?.name ?? '',
    brand_name: dept?.brand_name ?? '',
    memo: typeof dept?.memo === 'string' ? dept.memo : (dept?.memo ?? ''),
  }))
}

const cleanCompanyInfo = (data: CompanyRequestType): CompanyRequestType => {
  const cleanedData: CompanyRequestType = { ...data }

  // 1. 日付のフォーマットとnull変換
  cleanedData.contract_start_date = data.contract_start_date
    ? dayjs(data.contract_start_date).format('YYYY-MM-DD')
    : null
  cleanedData.contract_end_date = data.contract_end_date
    ? dayjs(data.contract_end_date).format('YYYY-MM-DD')
    : null

  // 2. 契約金額のクリーニング
  cleanedData.contract_amount =
    data.contract_amount !== null && data.contract_amount !== undefined
      ? parseFloat(data.contract_amount.toString().replace(/,/g, ''))
      : null

  // 3. 部署情報のクリーニング
  if (Array.isArray(data.departments)) {
    cleanedData.departments = data.departments
      .filter(department => department.brand_name !== '' || department.name !== '')
      .map(department => ({
        ...(department.id !== undefined && department.id !== null && { id: department.id }),
        name: department.name,
        brand_name: department.brand_name,
        memo: (department.memo === '' || department.memo === undefined) ? null : department.memo,
      }))

    if (cleanedData.departments.length === 0) {
      cleanedData.departments = []
    }
  } else {
    cleanedData.departments = []
  }

  // 4. その他のプロパティのクリーニング（空文字列、undefinedをnullに変換）
  for (const key of Object.keys(cleanedData) as (keyof CompanyRequestType)[]) {
    const value = cleanedData[key]

    if (value === '' || value === undefined) {
      if (typeof value !== 'number' || value !== 0) {
        cleanedData[key] = null
      }
    }
  }

  return cleanedData
}

export function CompanyManage() {
  const { registerCompany, updateCompany, refreshCompany } = useContext(UpdateCompanyContext)
  const { company } = useContext(CompanyContext)
  const { companyId } = useParams()
  const navigate = useNavigate()

  const initializeCompanyInfo = (companyData?: Partial<CompanyRequestType> | CompanyResponseType | null): CompanyRequestType => {
    const data = (companyData ?? {}) as Partial<CompanyRequestType>
    const departmentsSource = (companyData as { departments?: unknown })?.departments

    return {
      company_name: data.company_name || '',
      address: data.address || '',
      phone_number: data.phone_number || '',
      post_code: data.post_code || undefined,
      toyoshima_sales_department: data.toyoshima_sales_department || '',
      toyoshima_sales_rep_name: data.toyoshima_sales_rep_name || '',
      company_memo: data.company_memo || '',
      planned_site_name: data.planned_site_name || '',
      planned_site_address: data.planned_site_address || '',
      planned_site_phone: data.planned_site_phone || undefined,
      planned_site_post_code: data.planned_site_post_code || undefined,
      planned_site_email: data.planned_site_email || '',
      planned_site_contact_name: data.planned_site_contact_name || '',
      planned_site_contact_position: data.planned_site_contact_position || '',
      contact_department: data.contact_department || '',
      contact_name: data.contact_name || '',
      contact_position: data.contact_position || '',
      contact_email: data.contact_email || '',
      contact_phone: data.contact_phone || '',
      billing_department: data.billing_department || '',
      billing_contact: data.billing_contact || '',
      billing_position: data.billing_position || '',
      billing_email: data.billing_email || '',
      billing_phone: data.billing_phone || '',
      contract_start_date: data.contract_start_date || '',
      contract_end_date: data.contract_end_date || '',
      contract_amount: data.contract_amount || 0,
      departments: normalizeDepartments(departmentsSource),
    }
  }

  const [companyInfo, setCompanyInfo] = useState<CompanyRequestType>(initializeCompanyInfo())
  const [errors, setErros] = useState<Record<string, boolean>>({})
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!company) return
    if (companyId === 'register') {
      setCompanyInfo(initializeCompanyInfo({}))
    } else {
      setCompanyInfo(initializeCompanyInfo(company))
    }
  }, [company, companyId])

  useEffect(() => {
    if (!companyId || companyId === 'register') return
    refreshCompany(companyId)
  }, [companyId, refreshCompany])

  const addDepartment = useCallback(() => {
    setCompanyInfo((prev) => ({ ...prev, departments: [...(prev.departments || []), { ...DEFAULT_DEPARTMENT }] }))
  }, [])

  const handleInputChange = useCallback((index: number, key: keyof DepartmentRequestType, value: string) => {
    setCompanyInfo((prev) => {
      const newDepartments = [...(prev.departments || [])]
      newDepartments[index] = { ...newDepartments[index], [key]: value }
      return { ...prev, departments: newDepartments }
    })
  }, [])

  const onClickBack = useCallback(() => { navigate(-1) }, [navigate])

  const onClickDelete = useCallback((index: number) => {
    const newDepartments = [...(companyInfo.departments || [])]
    newDepartments.splice(index, 1)
    setCompanyInfo({ ...companyInfo, departments: newDepartments })
  }, [companyInfo])

  const onClickSubmit = useCallback(async () => {
    if (!companyId) return

    const newErrors: Record<string, boolean> = {}
    if (!companyInfo.contact_email) {
      newErrors.contact_email = true
    }

    if (Object.keys(newErrors).length > 0) {
      setErros(newErrors)
      Modal.error({
        content:
          <div className="flex flex-col">
            <span>窓口担当者のメールアドレスを入力して下さい。</span>
          </div>,
        onOk() { },
        centered: true
      })
      return
    }

    let deptInComplete = false
    companyInfo.departments?.forEach((dept, i) => {
      if (!dept.brand_name) {
        newErrors[`department_${i}_brand_name`] = true
        deptInComplete = true
      }
      if (!dept.name) {
        newErrors[`department_${i}_name`] = true
        deptInComplete = true
      }
    })
    if (deptInComplete) {
      setErros(newErrors)
      Modal.error({ content: '部署のブランド名と部署名を全て入力して下さい。', onOk() { }, centered: true })
      return
    }

    const phoneReg = /^[0-9]{10,11}$/
    const phoneFields: (keyof CompanyRequestType)[] = [
      'phone_number', 'contact_phone', 'billing_phone', 'planned_site_phone'
    ]

    let phoneError = false
    phoneFields.forEach(key => {
      const value = companyInfo[key]
      if (typeof value === 'string' && value && !phoneReg.test(value)) { // 値がある場合のみチェック
        newErrors[key] = true
        phoneError = true
      }
    })

    if (phoneError) {
      setErros(newErrors)
      Modal.error({ content: '電話番号はハイフン無し半角数字で入力して下さい', onOk() { }, centered: true })
      return
    }

    const emailReg = /^[\w.%+-]+@[\w.-]+\.[a-zA-Z]{2,}$/
    const emailFields: (keyof CompanyRequestType)[] = [
      'contact_email', 'billing_email', 'planned_site_email'
    ]

    let emailError = false
    emailFields.forEach(key => {
      const value = companyInfo[key]
      if (typeof value === 'string' && value && !emailReg.test(value)) { // 値がある場合のみチェック
        newErrors[key] = true
        emailError = true
      }
    })

    if (emailError) {
      setErros(newErrors)
      Modal.error({ content: 'メールアドレスを確認して下さい。', onOk() { }, centered: true })
      return
    }

    const postCodeReg = /^[0-9]{7}$/
    const postCodeFields: (keyof CompanyRequestType)[] = [
      'post_code', 'planned_site_post_code'
    ]

    let postCodeError = false
    postCodeFields.forEach(key => {
      const value = companyInfo[key]
      const stringValue = typeof value === 'number' ? value.toString() : value
      if (typeof stringValue === 'string' && stringValue && !postCodeReg.test(stringValue)) {
        newErrors[key] = true
        postCodeError = true
      }
    })

    if (postCodeError) {
      setErros(newErrors)
      Modal.error({ content: '郵便番号は半角数字7桁で入力して下さい。', onOk() { }, centered: true })
      return
    }

    setErros({})

    setIsLoading(true)

    try {

      const cleaned = cleanCompanyInfo(companyInfo)

      if (companyId === 'register') {
        await registerCompany(cleaned)
        Modal.success({ content: '企業の登録が完了しました。', onOk() { navigate(-1) }, centered: true })
      } else {
        await updateCompany(companyId, cleaned)
        Modal.success({ content: '企業情報の更新が完了しました。', onOk() { navigate(PATH_INFO.COMPANY_INFO.PATH.replace(':companyId', companyId)) }, centered: true })
      }
    } catch (error) {
      console.error("Operation failed:", error)
      Modal.error({ content: '処理中にエラーが発生しました。再度お試しください。', centered: true })
    } finally {
      setIsLoading(false)
    }
  }, [companyId, companyInfo, registerCompany, updateCompany, navigate])

  return (
    <div className="relative">
      {isLoading && (
        <Spin spinning={isLoading} tip="処理中..." size="large" fullscreen />
      )}
      <div>
        <div className="flex w-full space-x-5 items-center">
          <p className="flex text-2xl decoration-solid ml-2 mt-0 mb-0 border-0 border-l-4 border-solid border-[#1677ff] p-2">
            {`企業${companyId === 'register' ? '登録' : '編集'}画面`}
          </p>
        </div>
        <Divider />
        <CompanyInput
          companyInfo={companyInfo}
          setCompanyInfo={setCompanyInfo}
          errors={errors}
        />
        <Divider />
        <div className="flex p-2 text-xl mb-3">
          <ProfileOutlined className="text-[#1677ff]" />
          <span className="pl-2">{`部署${companyId === 'register' ? '登録' : '編集'}`}</span>
        </div>
        <Row justify={'center'} align={'top'} className="w-full" gutter={[24, 24]}>
          {companyInfo.departments?.map((item, index) => (
            <Col key={index}>
              <List
                bordered
                header={<div className="text-center font-bold">{index + 1}</div>}
                footer={<div className="text-center"><Button variant="outlined" danger onClick={() => onClickDelete(index)}>削除</Button></div>}
                dataSource={[
                  { key: 'brand_name', label: 'ブランド名', value: item.brand_name, type: 'text' },
                  { key: 'name', label: '部署名', value: item.name, type: 'text' },
                  { key: 'memo', label: '部署メモ', value: item.memo, type: 'textarea' },
                ]}
                renderItem={(department) => (
                  <List.Item style={{ background: '#FFFFFF' }}>
                    {department.type === 'text' ?
                      (<Input
                        placeholder={department.label}
                        type={department.type}
                        value={department.value || ''}
                        status={errors[`department_${index}_${department.key}`] ? 'error' : undefined}
                        onChange={(e) => handleInputChange(index, department.key as keyof DepartmentRequestType, e.target.value)}
                      />
                      )
                      :
                      (<TextArea
                        rows={6}
                        placeholder={department.label}
                        value={department.value as string}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                          handleInputChange(index, department.key as keyof DepartmentRequestType, e.target.value)
                        }}
                      />)
                    }
                  </List.Item>
                )}
                className="overflow-hidden bg-[#FAFAFA]"
              />
            </Col>
          ))}
        </Row>
        <Flex vertical align="center" className="h-full mt-5">
          <Button variant="text" color="primary" onClick={addDepartment} icon={<PlusCircleOutlined />}>部署を追加</Button>
        </Flex>
        <Divider />
        <Flex gap="large" align="center" justify="center">
          <Button variant="outlined" color="primary" size="large" onClick={onClickBack} disabled={isLoading}>戻る</Button>
          <Button type="primary" size="large" onClick={onClickSubmit} loading={isLoading}>{companyId === 'register' ? '登録' : '更新'}</Button>
        </Flex>
      </div>
    </div>
  )
}

function CompanyInput(
  {
    companyInfo,
    setCompanyInfo,
    errors
  }: {
    companyInfo: CompanyRequestType,
    setCompanyInfo: React.Dispatch<React.SetStateAction<CompanyRequestType>>
    errors: Record<string, boolean>
  }) {

  const COMPANY_DATA_PLACEHOLDER = [
    {
      title: '企業情報',
      column: [
        { label: '企業名', type: 'text', value: companyInfo.company_name, name: 'company_name' },
        { label: '住所', type: 'text', value: companyInfo.address, name: 'address' },
        { label: '電話番号 (ハイフン無し)', type: 'text', value: companyInfo.phone_number, name: 'phone_number' },
        { label: '郵便番号 (ハイフン無し)', type: 'text', value: companyInfo.post_code, name: 'post_code' },
        { label: '豊島営業部', type: 'text', value: companyInfo.toyoshima_sales_department, name: 'toyoshima_sales_department' },
        { label: '豊島営業担当者名', type: 'text', value: companyInfo.toyoshima_sales_rep_name, name: 'toyoshima_sales_rep_name' },
      ]
    },
    {
      title: '導入予定拠点',
      column: [
        { label: '拠点名', type: 'text', value: companyInfo.planned_site_name, name: 'planned_site_name' },
        { label: '住所', type: 'text', value: companyInfo.planned_site_address, name: 'planned_site_address' },
        { label: '電話番号 (ハイフン無し)', type: 'text', value: companyInfo.planned_site_phone, name: 'planned_site_phone' },
        { label: '郵便番号 (ハイフン無し)', type: 'text', value: companyInfo.planned_site_post_code, name: 'planned_site_post_code' },
        { label: 'メールアドレス', type: 'email', value: companyInfo.planned_site_email, name: 'planned_site_email' },
        { label: '担当者名', type: 'text', value: companyInfo.planned_site_contact_name, name: 'planned_site_contact_name' },
        { label: '担当者役職', type: 'text', value: companyInfo.planned_site_contact_position, name: 'planned_site_contact_position' },
      ]
    },
    {
      title: '窓口担当者',
      column: [
        { label: '部署名', type: 'text', value: companyInfo.contact_department, name: 'contact_department' },
        { label: '氏名', type: 'text', value: companyInfo.contact_name, name: 'contact_name' },
        { label: '役職', type: 'text', value: companyInfo.contact_position, name: 'contact_position' },
        { label: 'メールアドレス', type: 'email', value: companyInfo.contact_email, name: 'contact_email' },
        { label: '電話番号 (ハイフン無し)', type: 'text', value: companyInfo.contact_phone, name: 'contact_phone' }
      ]
    },
    {
      title: '請求先情報',
      column: [
        { label: '担当部署名', type: 'text', value: companyInfo.billing_department, name: 'billing_department' },
        { label: '担当者名', type: 'text', value: companyInfo.billing_contact, name: 'billing_contact' },
        { label: '担当者役職', type: 'text', value: companyInfo.billing_position, name: 'billing_position' },
        { label: 'メールアドレス', type: 'email', value: companyInfo.billing_email, name: 'billing_email' },
        { label: '電話番号 (ハイフン無し)', type: 'text', value: companyInfo.billing_phone, name: 'billing_phone' }
      ]
    },
    {
      title: '契約期間',
      column: [
        { label: '開始日', type: 'date', value: companyInfo.contract_start_date, name: 'contract_start_date' },
        { label: '終了予定日', type: 'date', value: companyInfo.contract_end_date, name: 'contract_end_date' },
        { label: '販売金額', type: 'money', prefix: '￥', suffix: 'JPY', value: companyInfo.contract_amount, name: 'contract_amount' }
      ]
    },
    {
      title: '企業メモ',
      column: [
        { label: '企業メモ', type: 'textarea', value: companyInfo.company_memo, name: 'company_memo' }
      ]
    }
  ]

  const dateFormat = 'YYYY/MM/DD'

  return (
    <Row justify={'center'} align={'top'} className="w-full" gutter={[24, 24]}>
      {COMPANY_DATA_PLACEHOLDER.map((section) => (
        <Col xs={12} xl={6} key={section.title}>
          <List
            header={<div className="font-bold">{section.title}</div>}
            bordered
            dataSource={section.column}
            renderItem={(item) => (
              <List.Item style={{ background: '#FFFFFF' }}>
                {item.type === 'date' &&
                  <DatePicker
                    placeholder={item.label}
                    format={dateFormat}
                    className="w-full"
                    status={errors[item.name] ? 'error' : undefined}
                    value={item.value ? dayjs(item.value) : null}
                    onChange={(date, dateString) => {
                      const name = item.name as keyof CompanyRequestType
                      setCompanyInfo((prev) => ({ ...prev, [name]: dateString }))
                    }}
                  />}
                {item.type === 'money' &&
                  <InputNumber<number>
                    prefix='￥'
                    placeholder={item.label}
                    controls={false}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    className="w-full"
                    status={errors[item.name] ? 'error' : undefined}
                    value={item.value === 0 || item.value === null || item.value === undefined ? null : Number(item.value)}
                    onChange={(value) => {
                      const name = item.name as keyof CompanyRequestType
                      setCompanyInfo((prev) => ({ ...prev, [name]: value }))
                    }}
                  />}
                {(item.type === 'text' || item.type === 'email') &&
                  <Input
                    placeholder={item.label}
                    type={item.type}
                    name={item.name}
                    value={item.value || ''}
                    status={errors[item.name] ? 'error' : undefined}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const { name, value } = e.target
                      setCompanyInfo((prev) => ({ ...prev, [name as keyof CompanyRequestType]: value }))
                    }}
                  />}
                {item.type === 'textarea' &&
                  <TextArea
                    rows={6}
                    name={item.name}
                    placeholder={item.label}
                    value={item.value as string}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                      const { name, value } = e.target
                      setCompanyInfo((prev) => ({ ...prev, [name as keyof CompanyRequestType]: value }))
                    }}
                  />}
              </List.Item>)}
            className="overflow-hidden bg-[#FAFAFA]"
          />
        </Col>
      ))}
    </Row>
  )
}